import { Anchor, Badge, Box, Group, Stack, Text } from '@mantine/core';
import {
    AttractionStopBrief,
    StopBriefVerdict,
} from '@routellers/types/dist/city-attraction/attraction-stop-brief';

const verdicts: Record<StopBriefVerdict, { label: string; color: string }> = {
    strong: { label: 'Стоит остановиться', color: 'green' },
    brief: { label: 'Коротко по пути', color: 'blue' },
    weak: { label: 'Слабая для этого маршрута', color: 'orange' },
    uncertain: { label: 'Недостаточно данных', color: 'gray' },
};
const truth: Record<string, string> = {
    confirmed: 'Подтверждено в досье',
    legend: 'Легенда',
    disputed: 'Спорно',
    unverified: 'Не подтверждено',
    unchecked: 'Не проверено',
};

export function AttractionStopBriefCard({
    brief,
    routeId,
    title,
}: {
    brief?: AttractionStopBrief | null;
    routeId: number | null;
    title: string;
}) {
    if (!brief)
        return (
            <Box p="md" style={{ border: '1px solid #dee2e6', borderRadius: 12 }}>
                <Text fw={700}>Стоит ли остановиться?</Text>
                <Text size="sm" c="dimmed">
                    Краткая карточка ещё не подготовлена. Это не означает, что место неинтересное —
                    откройте детали и банк фактов.
                </Text>
            </Box>
        );
    const fit = brief.routeFits.find((r) => r.routeId === routeId);
    const verdict = verdicts[fit?.verdict ?? brief.verdict];
    const evidence = (ids: number[]) =>
        ids.length > 0 ? (
            <details style={{ fontSize: 12, marginTop: 5 }}>
                <summary style={{ cursor: 'pointer', color: '#1971c2' }}>
                    Основания и источники · {ids.length}
                </summary>
                <Stack gap="xs" mt="xs">
                    {ids.map((id) => {
                        const fact = brief.evidence.find((f) => f.factId === id);
                        if (!fact) return null;
                        return (
                            <Box key={id} pl="xs" style={{ borderLeft: '2px solid #dee2e6' }}>
                                <Text size="xs" fw={600}>
                                    {truth[fact.truthStatus] || 'Не проверено'} · факт #{id}
                                </Text>
                                <Text size="xs">{fact.claim}</Text>
                                {fact.sources
                                    .filter((s) => /^https?:\/\//.test(s.url || ''))
                                    .map((s, i) => (
                                        <Box key={i} mt={4}>
                                            <Anchor
                                                size="xs"
                                                href={s.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {s.title || new URL(s.url).hostname}
                                            </Anchor>
                                            {s.quote && (
                                                <Text size="xs" c="dimmed" mt={2}>
                                                    «{s.quote}»
                                                </Text>
                                            )}
                                        </Box>
                                    ))}
                            </Box>
                        );
                    })}
                </Stack>
            </details>
        ) : null;
    return (
        <Box
            component="section"
            aria-label="Краткая карточка остановки"
            p="md"
            style={{ border: '1px solid #a5d8ff', borderRadius: 12, background: '#f8fbff' }}
        >
            <Stack gap="sm">
                <Group justify="space-between">
                    <Text fw={700}>Стоит ли остановиться?</Text>
                    <Badge color={verdict.color} variant="light">
                        {verdict.label}
                    </Badge>
                </Group>
                <Text size="sm" fw={600}>
                    {title}
                </Text>
                <Box>
                    <Text size="xs" fw={700} c="dimmed">
                        ЗАЧЕМ ОСТАНОВИТЬСЯ
                    </Text>
                    <Text size="sm">{brief.whyStop}</Text>
                </Box>
                <Box>
                    <Text size="xs" fw={700} c="dimmed">
                        {brief.facts.length === 3
                            ? '3 САМЫХ ИНТЕРЕСНЫХ ФАКТА'
                            : 'ЧТО УДАЛОСЬ ПОДТВЕРДИТЬ'}
                    </Text>
                    {brief.facts.length ? (
                        <Stack gap="xs" mt={5}>
                            {brief.facts.map((f, i) => (
                                <Box key={f.factId}>
                                    <Text size="sm">
                                        {i + 1}. {f.text}
                                    </Text>
                                    {evidence([f.factId])}
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Text size="sm" c="dimmed">
                            В досье пока нет фактов с источниками для уверенного отбора.
                        </Text>
                    )}
                </Box>
                <Box>
                    <Text size="xs" fw={700} c="dimmed">
                        ЧТО ВИДНО СЕЙЧАС
                    </Text>
                    <Text size="sm">{brief.visibleNow}</Text>
                    {evidence(brief.visibleFactIds)}
                </Box>
                <Box>
                    <Text size="xs" fw={700} c="dimmed">
                        ЧТО ДОБАВЛЯЕТ МАРШРУТУ
                    </Text>
                    <Text size="sm">{fit?.text || brief.contribution}</Text>
                    {!fit && (
                        <Text size="xs" c="dimmed">
                            Общая оценка: состав этого маршрута отдельно не оценён.
                        </Text>
                    )}
                    {evidence(fit?.factIds || brief.rationaleFactIds)}
                </Box>
                <Box>
                    <Text size="xs" fw={700} c="dimmed">
                        АРГУМЕНТ РЕДАКТОРА
                    </Text>
                    <Text size="sm">{brief.verdictReason}</Text>
                    {evidence(brief.rationaleFactIds)}
                </Box>
                {brief.caveat && (
                    <Text size="sm" c="orange.9">
                        {brief.caveat}
                    </Text>
                )}
                <Text size="xs" c="dimmed">
                    Редакторская оценка по досье ·{' '}
                    {new Date(brief.updatedAt).toLocaleDateString('ru-RU')}. Доступность на месте не
                    подтверждает. При изменении состава маршрута оценку нужно пересмотреть.
                </Text>
            </Stack>
        </Box>
    );
}
